import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import MobileWaffleGrid from './components/MobileWaffleGrid';
import MobileLegend from './components/MobileLegend';
import MobileStats from './components/MobileStats';
import MobileRefreshControl from './components/MobileRefreshControl';
import MobileGestureHelper from './components/MobileGestureHelper';
import MobileOfflineIndicator from './components/MobileOfflineIndicator';
import MobileQuickActions from './components/MobileQuickActions';
import { fetchMetrics } from '../../data';

const MobileResponsiveView = () => {
  const [isOnline, setIsOnline] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setIsRefreshing(true);
      const metrics = await fetchMetrics();
      
      const ratio = (metrics?.stablecoins_usd / metrics?.m2_usd) * 100;
      
      setDashboardData({
        stablecoinValue: metrics?.stablecoins_usd,
        m2Value: metrics?.m2_usd,
        lastUpdated: metrics?.last_updated,
        ratio,
        stablecoinPercentage: ratio,
        m2Percentage: 100 - ratio
      });
      
      setError(null);
    } catch (err) {
      setError('Failed to load data');
      console.error('Data fetch error:', err);
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Set up auto-refresh every 15 minutes
    const refreshInterval = setInterval(fetchData, 15 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  const handleRefresh = async () => {
    await fetchData();
  };

  const handleShare = () => {
    setShowShareModal(true);
    setTimeout(() => setShowShareModal(false), 3000);
  };

  const handleExport = () => {
    setShowExportModal(true);
    setTimeout(() => setShowExportModal(false), 3000);
  };

  const handleSettings = () => {
    alert('Settings panel would open here');
  };

  return (
    <>
      <Helmet>
        <title>Mobile View - StablecoinM2Tracker</title>
        <meta
          name="description"
          content="Mobile-optimized view of stablecoin to M2 money supply ratio with touch-friendly interactions and responsive design"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <Header />

        <main className="flex-1 pt-16">
          <MobileRefreshControl onRefresh={handleRefresh} />
          <MobileOfflineIndicator isOnline={isOnline} lastCachedUpdate={dashboardData?.lastUpdated} />
          <MobileGestureHelper />

          {isLoading ? (
            <div className="container mx-auto px-4 py-6 flex items-center justify-center min-h-[50vh]">
              <div className="flex items-center gap-3">
                <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                <span className="text-muted-foreground">Loading data...</span>
              </div>
            </div>
          ) : error ? (
            <div className="container mx-auto px-4 py-6 flex items-center justify-center min-h-[50vh]">
              <div className="text-center">
                <Icon name="AlertCircle" size={32} color="var(--color-error)" className="mx-auto mb-2" />
                <p className="text-error">{error}</p>
              </div>
            </div>
          ) : (
            <div className="container mx-auto px-4 py-6 md:py-8 lg:py-12">
              <div className="mb-6 md:mb-8">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
                    Mobile Dashboard
                  </h1>
                  {isRefreshing && (
                    <div className="flex items-center gap-2 text-primary">
                      <Icon name="Loader2" size={20} className="animate-spin" />
                      <span className="text-sm font-medium">Updating...</span>
                    </div>
                  )}
                </div>
                <p className="text-sm md:text-base text-muted-foreground">
                  Touch-optimized visualization of stablecoin market penetration relative to M2 money supply
                </p>
              </div>

              <div className="space-y-6 md:space-y-8">
                <MobileLegend
                  stablecoinPercentage={dashboardData?.stablecoinPercentage}
                  m2Percentage={dashboardData?.m2Percentage}
                />

                <div className="bg-card rounded-lg border border-border overflow-hidden">
                  <div className="bg-muted px-4 py-3 border-b border-border">
                    <div className="flex items-center justify-between">
                      <h2 className="text-base md:text-lg font-semibold text-foreground">
                        Interactive Waffle Chart
                      </h2>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Grid3x3" size={16} />
                        <span>100 tiles</span>
                      </div>
                    </div>
                  </div>
                  <MobileWaffleGrid
                    stablecoinPercentage={dashboardData?.stablecoinPercentage}
                    m2Percentage={dashboardData?.m2Percentage}
                  />
                  <div className="bg-muted px-4 py-3 border-t border-border">
                    <p className="text-xs text-muted-foreground text-center">
                      Tap any tile for detailed information
                    </p>
                  </div>
                </div>

                <MobileStats
                  stablecoinValue={dashboardData?.stablecoinValue}
                  m2Value={dashboardData?.m2Value}
                  ratio={dashboardData?.ratio}
                  lastUpdated={dashboardData?.lastUpdated}
                />

                <div className="bg-card rounded-lg border border-border p-4 md:p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded flex items-center justify-center">
                      <Icon name="Info" size={20} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                        About This Data
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        This visualization represents the relationship between total stablecoin supply and the US M2 money supply. Each tile in the grid represents 1% of the total M2 supply, with amber tiles showing stablecoin penetration.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-muted rounded p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="Database" size={16} color="var(--color-muted-foreground)" />
                        <span className="text-xs font-medium text-foreground">Data Source</span>
                      </div>
                      <p className="text-xs text-muted-foreground">DeFillama & FRED</p>
                    </div>
                    <div className="bg-muted rounded p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="RefreshCw" size={16} color="var(--color-muted-foreground)" />
                        <span className="text-xs font-medium text-foreground">Update Freq</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Real-time</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <MobileQuickActions
            onShare={handleShare}
            onExport={handleExport}
            onSettings={handleSettings}
          />

          {showShareModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-fade-in">
              <div className="bg-card rounded-lg border-2 border-primary shadow-2xl p-6 max-w-sm w-11/12 animate-scale-in">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={32} color="var(--color-success)" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-foreground text-center mb-2">
                  Share Link Copied!
                </h3>
                <p className="text-sm text-muted-foreground text-center">
                  The dashboard link has been copied to your clipboard
                </p>
              </div>
            </div>
          )}

          {showExportModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-fade-in">
              <div className="bg-card rounded-lg border-2 border-primary shadow-2xl p-6 max-w-sm w-11/12 animate-scale-in">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                    <Icon name="Download" size={32} color="var(--color-success)" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-foreground text-center mb-2">
                  Export Started!
                </h3>
                <p className="text-sm text-muted-foreground text-center">
                  Your data export is being prepared for download
                </p>
              </div>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default MobileResponsiveView;